import React, { useState, useEffect, useMemo } from 'react';
import { hasBusyTimes } from '../utils/bookingUtils';
import './DashboardCalendar.css';

const DashboardCalendar = ({ selectedDate, onDateSelect, busyTimes = [], venueTimezone = 'Europe/London' }) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate ? selectedDate.getMonth() : new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(selectedDate ? selectedDate.getFullYear() : new Date().getFullYear());

  // Sync current month/year with selectedDate when it changes externally
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate.getMonth());
      setCurrentYear(selectedDate.getFullYear());
    }
  }, [selectedDate]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate calendar dates
  const generateCalendarDates = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const dates = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      dates.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      dates.push(i);
    }

    return dates;
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    if (!day) return;
    const date = new Date(currentYear, currentMonth, day);
    onDateSelect(date);
  };

  const calendarDates = generateCalendarDates(currentYear, currentMonth);

  const busyDaysInMonth = useMemo(() => {
    const days = new Set();
    if (!busyTimes?.length) return days;
    const lastDay = new Date(currentYear, currentMonth + 1, 0).getDate();
    for (let day = 1; day <= lastDay; day += 1) {
      const date = new Date(currentYear, currentMonth, day);
      if (hasBusyTimes(date, busyTimes, venueTimezone)) {
        days.add(day);
      }
    }
    return days;
  }, [busyTimes, currentYear, currentMonth, venueTimezone]);

  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth &&
           selectedDate.getFullYear() === currentYear;
  };
  
  const hasBusyTime = (day) => Boolean(day && busyDaysInMonth.has(day));

  // Arrow icon SVG
  const ArrowIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="dashboard-calendar">
      {/* Month Header */}
      <div className="calendar-month-header">
        <div className="calendar-month-title">
          <p className="month-text">{months[currentMonth]} {currentYear}</p>
        </div>
        <div className="calendar-arrows">
          <button className="calendar-arrow-btn" onClick={handlePrevMonth}>
            <ArrowIcon />
          </button>
          <button className="calendar-arrow-btn calendar-arrow-right" onClick={handleNextMonth}>
            <ArrowIcon />
          </button>
        </div>
      </div>

      {/* Divider */}
      <div className="calendar-divider"></div>

      {/* Calendar Grid */}
      <div className="calendar-grid-container">
        {/* Weekday Headers */}
        <div className="calendar-weekdays">
          {weekdays.map((day) => (
            <div key={day} className="calendar-weekday-cell">
              <p className="weekday-text">{day}</p>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="calendar-days-grid">
          {calendarDates.map((day, index) => {
            const selected = isSelected(day);
            const busy = hasBusyTime(day);
            return (
              <div
                key={index}
                className={`calendar-day-cell ${!day ? 'empty' : ''} ${selected ? 'selected' : ''} ${busy ? 'has-busy' : ''}`}
                onClick={() => handleDateClick(day)}
              >
                {day && (
                  <p className={`day-text ${selected ? 'selected' : ''}`}>{day}</p>
                )}
                {selected && <div className="selected-day-bg"></div>}
                {busy && !selected && <div className="busy-indicator"></div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DashboardCalendar;

